import type { Authority } from "../auth/authorities";
import type { ProductId } from "../products/registry";

/**
 * The console's four sections, and the tabs under each.
 *
 * Structure comes from the F7 design: the rail switches SECTION, the tab strip
 * switches SUBPAGE. Everything below a section is a tab, not another sidebar
 * row — which is what keeps the sidebar four items long as the console grows.
 *
 * TABS ARE URLS, not component state. The design's prototype holds the current
 * tab in `this.state.surface`; we make each one addressable instead, because
 * the console's whole job during an incident is to be linked into a
 * conversation. "Look at /policy/caps/plus" works; "go to Policy then click
 * the third tab" does not survive being pasted into Slack.
 *
 * `needs` is documentation, NOT a gate. Nav never hides by authority — a page
 * the operator cannot read renders its own explanation, because a console
 * whose shape changes per person makes "why can't I see what you see?"
 * unanswerable. See Gate's docstring for the same argument at control level.
 */
export interface Tab {
  /** The URL. Must exist in ROUTES — a test asserts it. */
  to: string;
  label: string;
  /**
   * Extra path prefixes this tab is the parent of.
   *
   * A tab normally owns its own prefix, which is enough while a drill-in lives
   * under its parent's URL. It stopped being enough when the tool-caps INDEX
   * was absorbed into Tiers & credits and its EDITOR stayed at
   * `/policy/caps/{tier}`: that page's parent is now a tab whose path it does
   * not start with, so nothing lit and the editor sat outside the nav.
   */
  owns?: string[];
  /** What a reader of this file should know about who can use it. */
  needs?: Authority;
  /**
   * Which products this tab covers. The console is organised per product
   * because the system is: a merchant holds a tier, a status and an envelope
   * in EACH, set independently. A reader of the nav should be able to see
   * that a tab governs one product alone without opening it.
   */
  products: ProductId[];
}

export interface Section {
  id: string;
  label: string;
  /** One line under the section heading. From the design, verbatim. */
  blurb: string;
  /**
   * Heroicons 20/solid path data for the sidebar RAIL, verbatim from the
   * design's own icon() calls. The rail carries no text, so this is the only
   * thing distinguishing one section from another when the panel is collapsed.
   */
  icon: string[];
  tabs: Tab[];
}

export const SECTIONS: Section[] = [
  {
    id: "overview",
    label: "Overview",
    blurb: "Fleet posture and the decision queue",
    icon: [
      "M3 3.75A.75.75 0 0 1 3.75 3h.5a.75.75 0 0 1 .75.75V16h11.25a.75.75 0 0 1 0 1.5H3.75a.75.75 0 0 1-.75-.75V3.75Z",
      "M7.5 11.5a.75.75 0 0 1 .75.75v2.25h-1.5v-2.25a.75.75 0 0 1 .75-.75Zm4-3.5a.75.75 0 0 1 .75.75v5.75h-1.5V8.75a.75.75 0 0 1 .75-.75Zm4-3a.75.75 0 0 1 .75.75v8.75h-1.5V5.75a.75.75 0 0 1 .75-.75Z",
    ],
    // One tab, so the strip is not rendered — a single tab is a label with
    // extra steps.
    tabs: [{ to: "/", label: "Fleet posture", products: ["mcp", "labeeb"] }],
  },
  {
    id: "tenants",
    label: "Tenants",
    blurb: "Per-tenant records in both products",
    icon: [
      "M7 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM14.5 9a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM1.615 16.428a1.224 1.224 0 0 1-.569-1.175 6.002 6.002 0 0 1 11.908 0c.058.467-.172.92-.57 1.174A9.953 9.953 0 0 1 7 18a9.953 9.953 0 0 1-5.385-1.572ZM14.5 16h-.106c.07-.297.088-.611.048-.933a7.47 7.47 0 0 0-1.588-3.755 4.502 4.502 0 0 1 5.874 2.636.818.818 0 0 1-.36.98A7.465 7.465 0 0 1 14.5 16Z",
    ],
    tabs: [
      { to: "/tenants", label: "All tenants", needs: "tenant:read", products: ["mcp", "labeeb"] },
    ],
  },
  {
    id: "policy",
    label: "Policy",
    blurb: "What the assistant is entitled and configured to do",
    icon: [
      "M1 4a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V4ZM1 11a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1v-1ZM1 16a1 1 0 0 1 1-1h5a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1v-1Z",
    ],
    tabs: [
      // Each of these covers both products: MCP's tier documents, blocklist
      // and settings beside Labeeb's tiers, standing and settings.
      { to: "/policy/tiers", label: "Tiers & limits", needs: "quota:read", products: ["mcp", "labeeb"], owns: ["/policy/caps"] },
      { to: "/policy/access", label: "Access", needs: "tenant:read", products: ["mcp", "labeeb"], owns: ["/tenants/access"] },
      { to: "/policy/settings", label: "Settings", needs: "settings:read", products: ["mcp", "labeeb"], owns: ["/policy/config", "/policy/hitl", "/policy/welcome", "/policy/apps", "/policy/flags"] },
    ],
  },
  {
    id: "governance",
    label: "Governance",
    blurb: "Who may write what, and what was written",
    icon: [
      "M4.5 2A1.5 1.5 0 0 0 3 3.5v13A1.5 1.5 0 0 0 4.5 18h11a1.5 1.5 0 0 0 1.5-1.5V7.621a1.5 1.5 0 0 0-.44-1.06l-3.62-3.622A1.5 1.5 0 0 0 11.878 2H4.5Zm2 8.5a.75.75 0 0 1 .75-.75h5.5a.75.75 0 0 1 0 1.5h-5.5a.75.75 0 0 1-.75-.75Zm.75 2.75a.75.75 0 0 0 0 1.5h5.5a.75.75 0 0 0 0-1.5h-5.5Z",
    ],
    tabs: [
      // The design's two tabs, and only those. Product health folds into the
      // permission map (both answer "what can reach what"); MCP's settings
      // editor lives under Feature flags, beside the Labeeb flags it belongs
      // with; the tool catalog is a panel under Tool caps, which is the only
      // place its answer — what a cap would strand — is ever needed.
      { to: "/governance/permissions", label: "Permission map", products: ["mcp", "labeeb"] },
      { to: "/governance/audit", label: "Audit log", needs: "audit:read", products: ["mcp"] },
    ],
  },
];

/** Every tab, flattened — for route checks and the command palette. */
export const ALL_TABS: Tab[] = SECTIONS.flatMap((s) => s.tabs);

/**
 * Which section a path belongs to, for highlighting the rail.
 *
 * Longest match wins, so `/tenants/access` picks Tenants rather than falling
 * through. The tenant drill-in (`/tenants/:ref`) has no tab of its own and
 * still needs to light up Tenants — matching on the section's URL prefix
 * handles that without listing every sub-route.
 */
export function sectionFor(pathname: string): Section | undefined {
  if (pathname === "/") return SECTIONS[0];
  return SECTIONS.find((s) => s.id !== "overview" && pathname.startsWith(`/${s.id}`));
}

/**
 * Is this path one of a section's OWN tabs, or a drill-in below one?
 *
 * The distinction decides who owns the heading. On a tab, the section header
 * names the section and the strip names the tab, so a page title would say the
 * tab's name a second time. On a DRILL-IN — a merchant record, one tier's caps
 * — the strip names none of them, and suppressing the page's title there left
 * `/tenants/507077` headed "Tenants" with the merchant named nowhere on it.
 */
export function isTabPath(pathname: string): boolean {
  const section = sectionFor(pathname);
  return !!section && section.tabs.length > 1 && section.tabs.some((t) => t.to === pathname);
}
